import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import HighlightText from '@/components/base/HighlightText';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { hotSearches } from '@/mocks/site';
import { formatViews, getDetailPath, searchItems } from '@/utils/content';

const typeLabel: Record<string, { text: string; style: string }> = {
  news: { text: '新闻', style: 'bg-primary-100 text-primary-700' },
  service: { text: '生活服务', style: 'bg-accent-100 text-accent-800' },
  entertainment: { text: '娱乐', style: 'bg-secondary-200 text-secondary-800' },
};

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
  }, [query]);

  useDocumentMeta({
    title: query ? `【${query}】的搜索结果 - 91网站` : '全站搜索 - 91网站',
    description: `在91网站搜索「${query || '关键词'}」的相关新闻资讯、生活服务与娱乐内容,快速获取你关心的信息。`,
    keywords: `91网站,搜索,${query || '关键词'}`,
  });

  const results = useMemo(() => searchItems(query), [query]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  const goSearch = (word: string) => {
    setParams({ q: word });
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb items={[{ label: '全站搜索' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">全站搜索</h1>
          <form onSubmit={submit} className="mt-5 flex max-w-2xl items-center gap-2">
            <div className="flex flex-1 items-center rounded-full border border-background-300 bg-background-100 pl-4 pr-1 focus-within:border-primary-400 focus-within:bg-background-50">
              <i className="ri-search-line text-secondary-500"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                type="text"
                placeholder="搜索资讯 / 服务 / 娱乐内容"
                className="h-11 flex-1 bg-transparent px-3 text-sm text-foreground-900 outline-none placeholder:text-secondary-400"
              />
            </div>
            <button
              type="submit"
              className="flex h-11 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-6">
        {query ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm text-secondary-600">
              <span>关键词：</span>
              <span className="font-medium text-foreground-900">{query}</span>
              <span className="text-secondary-400">|</span>
              <span>
                共找到 <strong className="text-accent-600">{results.length}</strong> 条结果
              </span>
            </div>

            {results.length > 0 ? (
              <div className="mt-5 space-y-3">
                {results.map((item) => (
                  <Link
                    key={`${item.type}-${item.id}`}
                    to={getDetailPath(item)}
                    className="group flex gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:p-4"
                  >
                    <div className="h-24 w-32 shrink-0 overflow-hidden rounded-md md:h-28 md:w-44">
                      <img
                        src={item.image}
                        alt={item.title}
                        title={item.title}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${typeLabel[item.type].style}`}
                        >
                          {typeLabel[item.type].text}
                        </span>
                        <span className="text-[11px] text-secondary-400">{item.subCategory}</span>
                      </div>
                      <h2 className="mt-1.5 line-clamp-2 font-medium text-foreground-950 transition-colors group-hover:text-primary-600 md:text-[17px]">
                        <HighlightText text={item.title} keyword={query} />
                      </h2>
                      <p className="mt-1.5 line-clamp-2 hidden text-xs leading-relaxed text-secondary-500 sm:block">
                        <HighlightText text={item.summary} keyword={query} />
                      </p>
                      <div className="mt-auto flex items-center gap-4 pt-2 text-[11px] text-secondary-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {item.publishTime.slice(0, 10)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {formatViews(item.views)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <i className="ri-search-eye-line text-4xl text-secondary-300"></i>
                <p className="mt-3 text-sm text-secondary-600">
                  没有找到与「<span className="font-medium text-foreground-900">{query}</span>」相关的内容
                </p>
                <p className="mt-1 text-xs text-secondary-400">换个关键词试试，或看看下面的热门搜索</p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {hotSearches.map((word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => goSearch(word)}
                      className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-10">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-fire-line text-accent-500"></i>
              热门搜索
            </h2>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {hotSearches.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => goSearch(word)}
                  className="whitespace-nowrap rounded-full bg-background-100 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                >
                  {word}
                </button>
              ))}
            </div>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { to: '/category/news', icon: 'ri-newspaper-line', name: '新闻频道' },
                { to: '/category/life', icon: 'ri-home-smile-2-line', name: '生活频道' },
                { to: '/category/entertainment', icon: 'ri-movie-2-line', name: '娱乐频道' },
              ].map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${c.icon} text-lg`}></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-800">{c.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}